import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Lock, Mail, ArrowUpRight } from "lucide-react";
import { formatApiError } from "../lib/api";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-aimploy-dark relative overflow-hidden p-6" data-testid="admin-login-page">
            <div className="absolute inset-0 bg-dots-dark opacity-30" />
            <div className="absolute top-1/4 -right-40 h-[500px] w-[500px] rounded-full bg-aimploy-violet/20 blur-3xl" />

            <div className="relative w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
                    <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
                        <span className="font-display font-bold text-aimploy-navy text-lg">a.</span>
                    </div>
                    <span className="font-display font-bold text-2xl text-white">aimploy</span>
                </Link>

                <div className="bg-white rounded-3xl p-8 lg:p-10">
                    <div className="text-center mb-8">
                        <div className="inline-flex h-12 w-12 rounded-xl bg-aimploy-navy text-white items-center justify-center mb-4">
                            <Lock className="w-5 h-5" />
                        </div>
                        <h1 className="font-display text-3xl font-bold">Admin Login</h1>
                        <p className="text-sm text-aimploy-muted mt-2">Zugang für Team-Mitglieder</p>
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-aimploy-muted" />
                            <input
                                type="email"
                                placeholder="E-Mail"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="admin-login-email"
                                className="w-full border border-aimploy-navy/15 rounded-xl pl-11 pr-4 py-3.5"
                            />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-aimploy-muted" />
                            <input
                                type="password"
                                placeholder="Passwort"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="admin-login-password"
                                className="w-full border border-aimploy-navy/15 rounded-xl pl-11 pr-4 py-3.5"
                            />
                        </div>
                        {error && (
                            <div data-testid="admin-login-error" className="text-sm text-red-500 bg-red-50 rounded-xl p-3">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-6 py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                        >
                            {loading ? "Anmelden..." : "Anmelden"}
                            <ArrowUpRight className="w-4 h-4" />
                        </button>
                    </form>
                </div>

                <Link to="/" className="block mt-6 text-center text-sm text-white/60 hover:text-white">
                    ← Zurück zur Website
                </Link>
            </div>
        </div>
    );
}
